import type { EligibleProjectCollaborator, ProjectMemberView, RepositoryView } from "@baton/contracts";

/**
 * One row per person across every repository linked to a project. The list is merged from two
 * sources that overlap: the collaborators the GitHub mirror can see on the project's repositories,
 * and the members the project graph already carries. Everything here is pure so the merge, the
 * badge and the search filter can be tested without a DOM or a server.
 */

/**
 * The eligible-collaborator row. `githubLogin` and `signedIn` are loosened to optional on top of
 * the contract: a server that has not been restarted since they landed simply omits them, and
 * `undefined` must render as "unknown" rather than as a claim the UI made up.
 */
export interface EligibleCollaborator extends Omit<EligibleProjectCollaborator, "githubLogin" | "signedIn"> {
  githubLogin?: string | null | undefined;
  signedIn?: boolean | null | undefined;
}

export type PersonState = "owner" | "admin" | "member" | "invited" | "not-invited";

export const PERSON_STATE_LABELS: Record<PersonState, string> = {
  owner: "Owner",
  admin: "Admin",
  member: "Member",
  invited: "Invited",
  "not-invited": "Not invited",
};

/** Roster order: the project's own people first, then everyone still outside it. */
const STATE_RANK: Record<PersonState, number> = { owner: 0, admin: 1, member: 2, invited: 3, "not-invited": 4 };

export interface PersonRepository { id: string; label: string }

export interface PersonRow {
  userId: string;
  handle: string;
  /** The GitHub login when the mirror reports one; otherwise null (the handle is the fallback). */
  githubLogin: string | null;
  /** true: has a Baton account. false: GitHub-only. null: the server has not said — never guessed. */
  signedIn: boolean | null;
  state: PersonState;
  /** Already on the project (owner, admin, member or invited). */
  onProject: boolean;
  /** Repositories of this project they can reach, as "owner/name". */
  repositories: PersonRepository[];
}

export interface MergePeopleInput {
  /** `GET /v1/projects/:id/eligible-collaborators`; empty for a viewer who may not read it. */
  collaborators: EligibleCollaborator[];
  /** The project graph's member list, so people already in the project appear too. */
  members: ProjectMemberView[];
  /** The graph's repositories, used to turn repository IDs into "owner/name". */
  repositories: Array<Pick<RepositoryView, "id" | "ownerHandle" | "name">>;
  /**
   * User IDs known to own a general agent. Signing in provisions one, so this is positive
   * evidence of a Baton account — never evidence of its absence.
   */
  signedInUserIds?: Iterable<string> | undefined;
}

export function mergePeople(input: MergePeopleInput): PersonRow[] {
  const labels = new Map(input.repositories.map((repository) => [repository.id, `${repository.ownerHandle}/${repository.name}`]));
  // A revoked membership is not a membership: those people fall back to "not invited".
  const members = new Map(input.members.filter((member) => member.state !== "revoked").map((member) => [member.userId, member]));
  const withAgent = new Set(input.signedInUserIds ?? []);

  const rows = new Map<string, PersonRow>();
  for (const collaborator of input.collaborators) {
    const membership = members.get(collaborator.userId) ?? collaborator.membership;
    const state = stateOf(membership);
    rows.set(collaborator.userId, {
      userId: collaborator.userId,
      handle: collaborator.handle,
      githubLogin: collaborator.githubLogin ?? null,
      signedIn: resolveSignedIn(collaborator.signedIn, state, withAgent.has(collaborator.userId)),
      state,
      onProject: state !== "not-invited",
      repositories: mapRepositories(collaborator.repositoryIds, labels),
    });
  }

  // Members the collaborator listing does not carry: they are on the project through an invite
  // link, so which repositories they reach is not something this response can say.
  for (const member of members.values()) {
    if (rows.has(member.userId)) continue;
    const state = stateOf(member);
    rows.set(member.userId, {
      userId: member.userId,
      handle: member.handle,
      githubLogin: null,
      signedIn: resolveSignedIn(undefined, state, withAgent.has(member.userId)),
      state,
      onProject: true,
      repositories: [],
    });
  }

  return [...rows.values()].sort((left, right) =>
    STATE_RANK[left.state] - STATE_RANK[right.state] || left.handle.localeCompare(right.handle));
}

function stateOf(membership: Pick<ProjectMemberView, "role" | "state"> | null | undefined): PersonState {
  if (!membership || membership.state === "revoked") return "not-invited";
  if (membership.state === "invited") return "invited";
  return membership.role;
}

/**
 * The server's own answer wins. Otherwise an accepted membership proves an account (accepting
 * requires signing in), and a general agent proves one too. Anything else stays unknown so the
 * row can omit the claim instead of making one up.
 */
function resolveSignedIn(reported: boolean | null | undefined, state: PersonState, hasGeneralAgent: boolean): boolean | null {
  if (typeof reported === "boolean") return reported;
  if (state === "owner" || state === "admin" || state === "member") return true;
  return hasGeneralAgent ? true : null;
}

function mapRepositories(repositoryIds: string[], labels: Map<string, string>): PersonRepository[] {
  const mapped: PersonRepository[] = [];
  for (const id of repositoryIds) {
    const label = labels.get(id);
    // A repository that left the project between the two reads is dropped rather than shown as a UUID.
    if (label) mapped.push({ id, label });
  }
  return mapped.sort((left, right) => left.label.localeCompare(right.label));
}

/** Case-insensitive search across the handle, the GitHub login and every repository label. */
export function filterPeople(rows: PersonRow[], query: string): PersonRow[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return rows;
  return rows.filter((row) =>
    row.handle.toLowerCase().includes(needle)
    || (row.githubLogin ?? "").toLowerCase().includes(needle)
    || row.repositories.some((repository) => repository.label.toLowerCase().includes(needle)));
}

export interface RepositorySummary { chips: PersonRepository[]; more: number; title: string }

/** At most `limit` chips; the rest become "+N more" with the full list in a title attribute. */
export function repositorySummary(repositories: PersonRepository[], limit = 2): RepositorySummary {
  return {
    chips: repositories.slice(0, limit),
    more: Math.max(0, repositories.length - limit),
    title: repositories.map((repository) => repository.label).join(", "),
  };
}

/** The quiet hint under a handle: account state first, then the repositories they can reach. */
export function personHint(row: PersonRow): string {
  const account = row.signedIn === true
    ? "uses Baton"
    : row.signedIn === false ? "GitHub only · signing in makes their account" : null;
  const reach = row.repositories.length === 0
    ? (row.onProject ? "on the project" : null)
    : `${row.repositories.length} shared ${row.repositories.length === 1 ? "repository" : "repositories"}`;
  return [account, reach].filter((part) => part !== null).join(" · ");
}

/**
 * The plain-text note copied for someone who has never signed in. It carries their handle, the
 * project, the single-use join link and a pointer to the dashboard's own Telegram button — never
 * the inviter's Telegram deep link, which is single-use and personal to them.
 */
export function inviteMessage(input: { handle: string; projectName: string; joinUrl: string }): string {
  return [
    `@${input.handle} — you are invited to "${input.projectName}" on Baton.`,
    `Join: ${input.joinUrl}`,
    "The link works once. After you join, press Telegram in the dashboard to connect Telegram and get your agents' messages on your phone.",
  ].join("\n");
}
